import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { restaurantInfo } from '../data/mock';

const Hero = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const scrollToMenu = () => {
    const target = document.getElementById('menu') || document.getElementById('explorar-menu');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
      window.history.pushState(null, '', '#menu');
    }
  };

  return (
    <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background con parallax */}
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ scale: 1.2 }}
        animate={{ scale: loaded ? 1 : 1.2 }}
        transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/28902883/pexels-photo-28902883.jpeg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
      </motion.div>

      {/* Grain texture overlay - hidden on mobile to leave only 1 clean background */}
      <div className="hidden md:block absolute inset-0 z-10 opacity-20 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=)'
        }}
      />

      {/* Content */}
      <div className="relative z-20 text-center px-6 max-w-5xl">
        {/* Animated reveal text */}
        <div className="overflow-hidden mb-6">
          <motion.h1
            className="hero-title text-8xl md:text-9xl font-black tracking-tighter"
            initial={{ y: 200, opacity: 0 }}
            animate={{ y: loaded ? 0 : 200, opacity: loaded ? 1 : 0 }}
            transition={{ 
              duration: 1.2, 
              delay: 0.3,
              ease: [0.16, 1, 0.3, 1] 
            }}
          >
            {restaurantInfo.name}
          </motion.h1>
        </div>

        <div className="overflow-hidden mb-8">
          <motion.p
            className="text-2xl md:text-3xl text-orange-400 font-light tracking-wide"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: loaded ? 0 : 100, opacity: loaded ? 1 : 0 }}
            transition={{ 
              duration: 1, 
              delay: 0.6,
              ease: [0.16, 1, 0.3, 1] 
            }}
          >
            {restaurantInfo.tagline}
          </motion.p>
        </div>

        <div className="overflow-hidden mb-12">
          <motion.p
            className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: loaded ? 0 : 80, opacity: loaded ? 1 : 0 }}
            transition={{ 
              duration: 1, 
              delay: 0.9,
              ease: [0.16, 1, 0.3, 1] 
            }}
          >
            {restaurantInfo.description}
          </motion.p>
        </div>

        <motion.button
          onClick={scrollToMenu}
          className="hero-cta group relative inline-flex items-center gap-3 px-10 py-5 text-lg font-bold text-black bg-gradient-to-r from-orange-400 to-amber-400 rounded-full overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/50"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: loaded ? 0 : 60, opacity: loaded ? 1 : 0 }}
          transition={{ 
            duration: 1, 
            delay: 1.2,
            ease: [0.16, 1, 0.3, 1] 
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="relative z-10">Explorar Menú</span>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-400"
            initial={{ x: '-100%' }}
            whileHover={{ x: 0 }}
            transition={{ duration: 0.3 }}
          />
        </motion.button>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 cursor-pointer"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : -20 }}
        transition={{ duration: 1, delay: 1.5 }}
        onClick={scrollToMenu}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-10 h-10 text-orange-400" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;